// @ts-strict-ignore
import { Page, pickSeoProps } from '@components/Page'
import RolexPage from '@components/_rolex/Page'
import { RolexPageIntro } from '@components/_rolex/PageIntro'
import { RolexHeroSlider } from '@components/_rolex/hero-slider'
import { RolexPushBannerGrid } from '@components/_rolex/push-banner-grid'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { Item, SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { RolexHomepageV72024 } from '@lib/kontent/models/RolexHomepageV72024'
import { RolexPageIntro as RolexPageIntroModel } from '@lib/kontent/models/RolexPageIntro'
import { RolexPushBannerGrid as RolexPushBannerGridModel } from '@lib/kontent/models/RolexPushBannerGrid'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { FC } from 'react'

type Props = SingleItemResponse<RolexHomepageV72024>

const RolexHomepage: FC<Props> = ({ item, modular_content }) => {
  const { locales, locale } = useRouter()
  const heroItems = item.elements.items.value.map((key) => modular_content[key].elements)
  const firstPushBanner = modular_content[
    item.elements.item.value[0]
  ] as Item<RolexPushBannerGridModel>
  const secondPushBanner = modular_content[
    item.elements.contact_push_banners.value[0]
  ] as Item<RolexPushBannerGridModel>
  const thirdPushBanner = modular_content[
    item.elements.discover_rolex_push_banners.value[0]
  ] as Item<RolexPushBannerGridModel>
  const keepExploringItems = item.elements.keep_exploring_slider_items.value.map(
    (key) => modular_content[key].elements,
  )
  const pageIntro = modular_content[item.elements.intro_section.value[0]]
    .elements as RolexPageIntroModel

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(locales, null, '/rolex')}
      noindex={locale === 'de'}
    >
      <RolexPage pageType="home page">
        <div className="bg-rolexLightBeige">
          <RolexHeroSlider items={heroItems} />
          <RolexPageIntro
            title={pageIntro.title.value}
            description={pageIntro.description}
            titleTag={pageIntro.title_tag.value[0]?.codename || 'h1'}
          />
          <RolexPushBannerGrid
            {...firstPushBanner.elements}
            banners={firstPushBanner.elements.items.value.map(
              (key) => modular_content[key].elements,
            )}
          />
          <RolexPushBannerGrid
            {...secondPushBanner.elements}
            banners={secondPushBanner.elements.items.value.map(
              (key) => modular_content[key].elements,
            )}
          />
          <RolexPushBannerGrid
            {...thirdPushBanner.elements}
            banners={thirdPushBanner.elements.items.value.map(
              (key) => modular_content[key].elements,
            )}
            className="bg-rolexBeige"
          />
          <KeepExploring className="bg-white py-[60px] lg:py-[90px]" items={keepExploringItems} />
        </div>
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  try {
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const content = await getPageByCodename<RolexHomepageV72024>({
      codename: 'rolex_homepage_367227d',
      lang: language,
      preview,
    })

    return {
      props: {
        ...(await getServerSideTranslations(locale)),
        ...content,
      },
    }
  } catch (error) {
    throw error
  }
}

export default RolexHomepage
